import React from "react";
import PropTypes from "prop-types";
import {
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import FilterMultiAutocomplete from "./filters/FilterMultiAutocomplete";

const lvGetLabel = (o) => (typeof o === "string" ? o : o.label);
const lvIsEq = (option, value) => option?.value === value?.value;

function lvRenderOption(props, option) {
  return (
    <li {...props} key={option.value}>
      {option.label}
    </li>
  );
}

/** Shared filter field grid — law firm, team-member archive, Bond Archives (`bondArchives`). */
export default function BondFilterFields({
  variant,
  filters,
  onArchiveFiltersChange = () => {},
  onLawFirmFiltersChange = () => {},
  onFiltersChange = () => {},
  bondTypes,
  availableSubcategories,
  bondNumberOptionsArchive,
  bondNumberOptionsLawFirm,
  availablePrincipals,
  availableEstates,
  principalFilterOptions,
  estateFilterOptions,
  lawFirms,
  attorneys,
  professionalGuardians,
  internalUsers,
  lawFirmStatusOptions,
  archiveStatusOptions,
  capitalizeFirst,
  statusOptions,
}) {
  if (variant === "bondArchives") {
    return (
      <>
        <FilterMultiAutocomplete
          label="Bond Type"
          options={bondTypes}
          value={filters.bondType}
          onChange={(v) => onFiltersChange({ ...filters, bondType: v })}
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Bond Sub Category"
          options={availableSubcategories}
          value={filters.bondSubCategory}
          onChange={(v) => onFiltersChange({ ...filters, bondSubCategory: v })}
        />
        <FilterMultiAutocomplete
          label="Bond Number"
          options={bondNumberOptionsArchive}
          value={filters.bondNumber}
          onChange={(v) => onFiltersChange({ ...filters, bondNumber: v })}
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Principal"
          options={availablePrincipals}
          value={filters.principal}
          onChange={(v) => onFiltersChange({ ...filters, principal: v })}
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Estate / Ward / Trust / Beneficiary"
          options={availableEstates}
          value={filters.estate}
          onChange={(v) => onFiltersChange({ ...filters, estate: v })}
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Law Firm"
          options={lawFirms}
          value={filters.lawFirm}
          onChange={(v) => onFiltersChange({ ...filters, lawFirm: v })}
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Attorney"
          options={attorneys}
          value={filters.attorney}
          onChange={(v) => onFiltersChange({ ...filters, attorney: v })}
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Professional Guardian"
          options={professionalGuardians}
          value={filters.professionalGuardian || []}
          onChange={(v) =>
            onFiltersChange({ ...filters, professionalGuardian: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Managed By"
          options={internalUsers}
          value={filters.internalUser}
          onChange={(v) =>
            onFiltersChange({ ...filters, internalUser: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Status"
          options={statusOptions ?? []}
          value={filters.status}
          onChange={(v) => onFiltersChange({ ...filters, status: v })}
        />
        <FormControl fullWidth>
          <InputLabel id="bond-archives-payment-status-filter-label">
            Payment Status
          </InputLabel>
          <Select
            labelId="bond-archives-payment-status-filter-label"
            label="Payment Status"
            value={filters.payment_status ?? ""}
            onChange={(e) =>
              onFiltersChange({ ...filters, payment_status: e.target.value })
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Paid">Paid</MenuItem>
            <MenuItem value="Unpaid">Unpaid</MenuItem>
            <MenuItem value="Unknown">Unknown</MenuItem>
            <MenuItem value="N/A">N/A</MenuItem>
          </Select>
        </FormControl>
        <TextField
          type="date"
          label="Start Date"
          value={filters.startDate || ""}
          onChange={(e) =>
            onFiltersChange({ ...filters, startDate: e.target.value })
          }
          slotProps={{ inputLabel: { shrink: true }}}
        />
        <TextField
          type="date"
          label="End Date"
          value={filters.endDate || ""}
          onChange={(e) =>
            onFiltersChange({ ...filters, endDate: e.target.value })
          }
          slotProps={{ inputLabel: { shrink: true }}}
        />
      </>
    );
  }

  const isArchive = variant === "archive";

  if (isArchive) {
    return (
      <>
        <FilterMultiAutocomplete
          label="Bond Type"
          options={bondTypes}
          value={filters.bondType}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, bondType: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Bond Sub Category"
          options={availableSubcategories}
          value={filters.bondSubCategory}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, bondSubCategory: v })
          }
        />
        <FilterMultiAutocomplete
          label="Bond Number"
          options={bondNumberOptionsArchive}
          value={filters.bondNumber}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, bondNumber: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Principal"
          options={availablePrincipals}
          value={filters.principal}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, principal: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Estate / Ward / Trust / Beneficiary"
          options={availableEstates}
          value={filters.estate}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, estate: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Law Firm"
          options={lawFirms}
          value={filters.lawFirm}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, lawFirm: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Attorney"
          options={attorneys}
          value={filters.attorney}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, attorney: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Professional Guardian"
          options={professionalGuardians}
          value={filters.professionalGuardian || []}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, professionalGuardian: v })
          }
          getOptionLabel={lvGetLabel}
          isOptionEqualToValue={lvIsEq}
          renderOption={lvRenderOption}
        />
        <FilterMultiAutocomplete
          label="Status"
          options={archiveStatusOptions}
          value={filters.underwriting_status}
          onChange={(v) =>
            onArchiveFiltersChange({ ...filters, underwriting_status: v })
          }
          getOptionLabel={(o) => capitalizeFirst(o)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={capitalizeFirst(option)}
              />
            ))
          }
        />
        <TextField
          type="date"
          label="Start Date"
          value={filters.startDate || ""}
          onChange={(e) =>
            onArchiveFiltersChange({
              ...filters,
              startDate: e.target.value,
            })
          }
          slotProps={{ inputLabel: { shrink: true }}}
        />
        <TextField
          type="date"
          label="End Date"
          value={filters.endDate || ""}
          onChange={(e) =>
            onArchiveFiltersChange({
              ...filters,
              endDate: e.target.value,
            })
          }
          slotProps={{ inputLabel: { shrink: true }}}
        />
      </>
    );
  }

  return (
    <>
      <FilterMultiAutocomplete
        label="Bond Type"
        options={bondTypes}
        value={filters.bondType}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({ ...f, bondType: v }))
        }
        getOptionLabel={lvGetLabel}
        isOptionEqualToValue={lvIsEq}
        renderOption={lvRenderOption}
      />
      <FilterMultiAutocomplete
        label="Bond Sub Category"
        options={availableSubcategories}
        value={filters.bondSubCategory}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            bondSubCategory: v,
          }))
        }
      />
      <FilterMultiAutocomplete
        label="Bond Number"
        options={bondNumberOptionsLawFirm}
        value={filters.bondNumber}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            bondNumber: v,
          }))
        }
        getOptionLabel={lvGetLabel}
        isOptionEqualToValue={lvIsEq}
        renderOption={lvRenderOption}
      />
      <FilterMultiAutocomplete
        label="Principal"
        options={principalFilterOptions}
        value={filters.principal}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            principal: v,
          }))
        }
        getOptionLabel={(o) => o?.label ?? ""}
        isOptionEqualToValue={(option, value) =>
          (option?.label ?? option?.value) ===
          (value?.label ?? value?.value)
        }
        renderOption={lvRenderOption}
      />
      <FilterMultiAutocomplete
        label="Estate / Ward / Trust / Beneficiary"
        options={estateFilterOptions}
        value={filters.estate}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            estate: v,
          }))
        }
        getOptionLabel={(o) => o?.label ?? ""}
        isOptionEqualToValue={(option, value) =>
          (option?.label ?? option?.value) ===
          (value?.label ?? value?.value)
        }
        renderOption={lvRenderOption}
      />
      <FilterMultiAutocomplete
        label="Law Firm"
        options={lawFirms}
        value={filters.lawFirm}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            lawFirm: v,
          }))
        }
        getOptionLabel={lvGetLabel}
        isOptionEqualToValue={lvIsEq}
        renderOption={lvRenderOption}
        disabled
        sx={{
          "& .MuiInputBase-root": {
            backgroundColor: "#f5f5f5",
          },
        }}
      />
      <FilterMultiAutocomplete
        label="Attorney"
        options={attorneys}
        value={filters.attorney}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            attorney: v,
          }))
        }
        getOptionLabel={lvGetLabel}
        isOptionEqualToValue={lvIsEq}
        renderOption={lvRenderOption}
      />
      <FilterMultiAutocomplete
        label="Status"
        options={lawFirmStatusOptions}
        value={filters.status}
        onChange={(v) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            status: v,
          }))
        }
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {option}
          </li>
        )}
      />
      <TextField
        label="Start Date"
        type="date"
        value={filters.startDate || ""}
        onChange={(e) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            startDate: e.target.value,
          }))
        }
        slotProps={{ inputLabel: { shrink: true }}}
      />
      <TextField
        label="End Date"
        type="date"
        value={filters.endDate || ""}
        onChange={(e) =>
          onLawFirmFiltersChange((f) => ({
            ...f,
            endDate: e.target.value,
          }))
        }
        slotProps={{ inputLabel: { shrink: true }}}
      />
    </>
  );
}

BondFilterFields.propTypes = {
  variant: PropTypes.oneOf(["archive", "lawFirm", "bondArchives"]).isRequired,
  filters: PropTypes.object.isRequired,
  onArchiveFiltersChange: PropTypes.func,
  onLawFirmFiltersChange: PropTypes.func,
  onFiltersChange: PropTypes.func,
  bondTypes: PropTypes.array.isRequired,
  availableSubcategories: PropTypes.array.isRequired,
  bondNumberOptionsArchive: PropTypes.array.isRequired,
  bondNumberOptionsLawFirm: PropTypes.array.isRequired,
  availablePrincipals: PropTypes.array.isRequired,
  availableEstates: PropTypes.array.isRequired,
  principalFilterOptions: PropTypes.array.isRequired,
  estateFilterOptions: PropTypes.array.isRequired,
  lawFirms: PropTypes.array.isRequired,
  attorneys: PropTypes.array.isRequired,
  professionalGuardians: PropTypes.array.isRequired,
  lawFirmStatusOptions: PropTypes.array.isRequired,
  archiveStatusOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  capitalizeFirst: PropTypes.func.isRequired,
  internalUsers: PropTypes.array,
  statusOptions: PropTypes.arrayOf(PropTypes.string),
};
